<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useDialogPluginComponent } from 'quasar';
import { useField, useForm } from 'vee-validate';
import * as yup from 'yup';

import type { ConsultationDto, Locales } from '@/services/types/api.types';
import { useLanguageValidationErrors } from '@/composables';
import { wrapSavingOperation } from '@/helpers/notifications.helper';
import { languageEnabledArray, localizableString } from '@/helpers/validation.helper';

import LanguageSwitcher from '@/components/LanguageSwitcher.vue';
import LocalizedInput from '@/components/LocalizedInput.vue';
import LocalizedEditor from '@/components/base/LocalizedEditor.vue';
import LanguageSelector from '@/components/LanguageSelector.vue';
import { useCommunityStore, useConsultationTemplatesStore } from '@/stores';
import { useI18n } from 'vue-i18n';
import { storeToRefs } from 'pinia';
import { Constants } from '@/constants';
import { getSupportedLocales } from '@/plugins/i18n';
import { provideEditLocale } from '@/composables/data-display-utils';

const { t } = useI18n();
const communityStore = useCommunityStore();
const consultationTemplatesStore = useConsultationTemplatesStore();

const { community } = storeToRefs(communityStore);

// PROPS
const props = defineProps<{
  consultationTemplate: ConsultationDto;
}>();

const languageEnabled = ref<Locales[]>(props.consultationTemplate.language_enabled);

// DIALOG SET-UP
defineEmits([...useDialogPluginComponent.emits]);
const { dialogRef, onDialogHide, onDialogOK, onDialogCancel } = useDialogPluginComponent();

// FORM
const { handleSubmit, errors, meta, setFieldValue, values } = useForm({
  initialValues: props.consultationTemplate,
  validationSchema: computed(() =>
    yup.object({
      title: localizableString(languageEnabled.value),
      description: localizableString(languageEnabled.value),
      language_enabled: languageEnabledArray(),
    }),
  ),
});

const languageErrors = useLanguageValidationErrors(errors);

const { value: languageEnabledFieldValue } = useField<Locales[]>('language_enabled');
const editLocale = provideEditLocale(languageEnabledFieldValue);
watch(languageEnabledFieldValue, () => {
  if (!languageEnabledFieldValue.value) return;
  getSupportedLocales()
    .filter((locale) => !languageEnabledFieldValue.value.includes(locale))
    .forEach((locale) => {
      setFieldValue(`title.${locale}`, undefined);
      setFieldValue(`description.${locale}`, undefined);
    });
  languageEnabledFieldValue.value.forEach((locale) => {
    if (!values.title?.[locale]) setFieldValue(`title.${locale}`, '');
    if (!values.description?.[locale]) setFieldValue(`description.${locale}`, '');
  });
  languageEnabled.value = languageEnabledFieldValue.value;
});

const onSubmit = handleSubmit(async (values) => {
  await wrapSavingOperation(
    async () => await consultationTemplatesStore.save(values as ConsultationDto),
    () => onDialogOK(),
  );
});
</script>

<template>
  <q-dialog ref="dialogRef" @hide="onDialogHide" persistent>
    <q-card class="q-dialog-plugin" v-if="props.consultationTemplate" style="width: 600px">
      <q-card-section class="row">
        <div class="text-h6">{{ $t('form.dialogs.consultationTemplate.create') }}</div>
        <q-space />
        <language-switcher
          v-model="editLocale"
          :has-validation-errors="languageErrors"
          :languageEnabled="languageEnabled"
        />
      </q-card-section>
      <q-separator />
      <q-card-section>
        <language-selector
          name="language_enabled"
          :label="t('consultationTemplate.language_enabled')"
          :language-enabled="community?.language_enabled"
        />
        <localized-input
          name="title"
          :label="$t('global.label.title')"
          :editLocale="editLocale"
          outlined
          :language_enabled="languageEnabled"
        />
        <q-field :label="$t('global.label.content')" stack-label borderless>
          <template v-slot:control>
            <localized-editor
              name="description"
              :label="$t('global.label.content')"
              class="q-mt-sm full-width"
              :editLocale="editLocale"
              :language_enabled="languageEnabled"
            />
          </template>
        </q-field>
      </q-card-section>
      <q-separator />
      <q-card-actions align="right">
        <q-btn color="primary" flat :label="$t('global.cancel')" @click="onDialogCancel" />
        <q-btn color="primary" :label="$t('global.save')" @click="onSubmit" />
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>
